import React from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'

interface ArticleContent {
  slug: string
  title: string
  category: string
  readTime: string
  publishDate: string
  image: string
  content: {
    introduction: string
    sections: {
      heading: string
      content: string
      tips?: string[]
    }[]
    conclusion: string
  }
}

const articles: Record<string, ArticleContent> = {
  'invoice-to-me-guide': {
    slug: 'invoice-to-me-guide',
    title: 'Invoice To Me: How to Bill Yourself or Clients as a Sole Trader',
    category: 'Best Practices',
    readTime: '9 min read',
    publishDate: '2024-02-12',
    image: 'https://images.pexels.com/photos/6863332/pexels-photo-6863332.jpeg?auto=compress&cs=tinysrgb&w=1200',
    content: {
      introduction: 'The phrase "invoice to me" or "invoice me" is commonly used in business communications, but it can mean different things depending on the context. Whether you\'re a freelancer, sole trader, or small business owner, understanding how to properly invoice yourself (for expense tracking) or respond to "please invoice me" requests is essential for professional operations.',
      sections: [
        {
          heading: '1. Understanding "Invoice To Me"',
          content: 'When someone says "invoice me," they\'re typically asking you to send a formal payment request. This could be a client requesting documentation for payment processing, or it could refer to self-invoicing for record-keeping purposes.',
          tips: [
            'Client requesting invoice: Send professional invoice with payment terms',
            'Self-invoicing: Document expenses or draws for accounting records',
            'Respond within 24 hours for professional impression',
            'Use proper invoice format with all required fields'
          ]
        },
        {
          heading: '2. How to Respond to Client "Invoice Me" Requests',
          content: 'When a client asks you to invoice them, they\'re ready to pay. This is your cue to send a professional, detailed invoice immediately.',
          tips: [
            'Generate invoice same day or next business day',
            'Include clear description of services/products',
            'State payment terms (NET 30, NET 60, etc.)',
            'Provide multiple payment methods',
            'Include payment link for convenience'
          ]
        },
        {
          heading: '3. Self-Invoicing for Sole Traders',
          content: 'Creating invoices to yourself might seem odd, but it\'s useful for tracking business expenses paid personally or documenting drawings from your business account.',
          tips: [
            'Document reimbursable business expenses',
            'Track personal draws from business account',
            'Create paper trail for tax purposes',
            'Attach receipts to self-invoices',
            'Store records for minimum 6 years'
          ]
        },
        {
          heading: '4. Essential Invoice Elements',
          content: 'Whether invoicing a client or yourself, include all necessary information to ensure the invoice is valid and professional.',
          tips: [
            'Sequential invoice number (INV-001, INV-002, etc.)',
            'Invoice date and payment due date',
            'Your business name and contact information',
            'Client/recipient name and details',
            'Itemized list of services/products',
            'Clear total amount due',
            'Payment instructions and accepted methods',
            'Tax information (VAT/GST if applicable)'
          ]
        },
        {
          heading: '5. Common Mistakes to Avoid',
          content: 'Avoid these pitfalls when creating invoices, especially in response to "invoice me" requests.',
          tips: [
            'Don\'t delay - send invoices promptly',
            'Avoid vague descriptions like "services rendered"',
            'Don\'t forget to include payment terms',
            'Never omit payment instructions',
            'Don\'t use self-invoices for improper tax deductions'
          ]
        },
        {
          heading: '6. Professional Communication Tips',
          content: 'How you present your invoice matters. Professional communication improves payment speed and maintains relationships.',
          tips: [
            'Use professional email subject line: "Invoice #12345"',
            'Include friendly greeting and thank you',
            'Clearly state amount due and due date',
            'Provide easy payment options',
            'Be available for questions'
          ]
        },
        {
          heading: '7. Payment Terms Best Practices',
          content: 'Set clear expectations for when and how payment should be made to avoid confusion and late payments.',
          tips: [
            'Standard NET 30 for most B2B transactions',
            'NET 15 for smaller projects or new clients',
            'Consider early payment discounts (2% for 10 days)',
            'State late payment penalties if applicable',
            'Include specific due date, not just "30 days"'
          ]
        }
      ],
      conclusion: 'Whether responding to a client\'s "invoice me" request or creating self-invoices for record-keeping, professionalism and clarity are key. Always use proper invoice formatting, respond quickly to client requests, and maintain detailed records. Tools like InvoiceMeNow.com make it easy to create professional invoices in seconds, ensuring you never miss an opportunity to get paid promptly.'
    }
  },
  'collect-outstanding-unpaid-invoices': {
    slug: 'collect-outstanding-unpaid-invoices',
    title: 'How to Collect Outstanding Invoices and Unpaid Invoices',
    category: 'Payment Collection',
    readTime: '8 min read',
    publishDate: '2024-02-10',
    image: 'https://images.pexels.com/photos/6289065/pexels-photo-6289065.jpeg?auto=compress&cs=tinysrgb&w=1200',
    content: {
      introduction: 'Outstanding invoices and unpaid bills can significantly impact your business cash flow. According to recent studies, small businesses lose billions annually due to late payments. This comprehensive guide will walk you through proven strategies to collect outstanding invoices while maintaining professional relationships with your clients.',
      sections: [
        {
          heading: '1. Prevention is Better Than Cure',
          content: 'The best way to handle unpaid invoices is to prevent them from happening in the first place. Set clear payment terms upfront, require deposits for large projects, and communicate expectations from day one.',
          tips: [
            'Include payment terms in your contract or agreement',
            'Request 25-50% deposit for projects over $1,000',
            'Send invoices immediately upon completion',
            'Use clear, professional language in all communications'
          ]
        },
        {
          heading: '2. Send Friendly Payment Reminders',
          content: 'Before escalating, send a polite reminder email. Many late payments are simply due to oversight or administrative delays. A friendly nudge often resolves the issue quickly.',
          tips: [
            'Send first reminder 3-5 days before due date',
            'Follow up on the due date if unpaid',
            'Send second reminder 7 days after due date',
            'Keep tone professional and friendly in early reminders'
          ]
        },
        {
          heading: '3. Pick Up the Phone',
          content: 'If emails go unanswered, a phone call can be highly effective. It\'s harder to ignore a personal conversation, and you can address any concerns or confusion immediately.',
          tips: [
            'Call between 10 AM - 3 PM for best response',
            'Be prepared with invoice details',
            'Ask if there are any issues with the invoice',
            'Offer flexible payment options if needed'
          ]
        },
        {
          heading: '4. Offer Payment Plans',
          content: 'If a client is experiencing financial difficulties, offering a payment plan can help you recover funds while maintaining the relationship. Break the total into manageable installments.',
          tips: [
            'Suggest 3-6 month payment plans',
            'Get the agreement in writing',
            'Consider charging a small interest fee',
            'Use automatic payment reminders'
          ]
        },
        {
          heading: '5. Use Invoice Automation Tools',
          content: 'Automation can significantly improve your collection rates. Set up automatic reminders, payment tracking, and recurring billing to reduce manual follow-up work.',
          tips: [
            'Enable automatic payment reminders',
            'Use online payment options for faster processing',
            'Track invoice status in real-time',
            'Generate reports on overdue accounts'
          ]
        },
        {
          heading: '6. Consider Legal Action (Last Resort)',
          content: 'If all else fails and the amount justifies it, you may need to consider legal action. This could include sending a formal demand letter, using a collection agency, or taking the matter to small claims court.',
          tips: [
            'Send formal demand letter first (certified mail)',
            'Consider collection agency for amounts over $500',
            'Small claims court for amounts under $10,000',
            'Consult with a business attorney for large amounts'
          ]
        }
      ],
      conclusion: 'Collecting outstanding invoices requires a balance of persistence and professionalism. Start with friendly reminders and escalate gradually. Most importantly, implement preventive measures to minimize future late payments. Use tools like InvoiceMeNow.com to streamline your invoicing process and automate payment reminders.'
    }
  },
  'invoice-message-email-best-practices': {
    slug: 'invoice-message-email-best-practices',
    title: 'What to Include in an Invoice Message or Email',
    category: 'Best Practices',
    readTime: '6 min read',
    publishDate: '2024-02-08',
    image: 'https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=1200',
    content: {
      introduction: 'The message you send with your invoice is just as important as the invoice itself. A well-crafted invoice email can significantly improve your payment turnaround time and strengthen client relationships. This guide covers everything you need to include in your invoice communications.',
      sections: [
        {
          heading: '1. Professional Subject Line',
          content: 'Your subject line should be clear, professional, and include the invoice number. This helps clients organize their emails and prioritize payments.',
          tips: [
            'Format: "Invoice #12345 from [Your Company]"',
            'Include the word "Invoice" for easy filtering',
            'Add invoice number for reference',
            'Keep it under 50 characters'
          ]
        },
        {
          heading: '2. Personal Greeting',
          content: 'Start with a personalized greeting using the client\'s name. This adds a human touch and shows attention to detail.',
          tips: [
            'Use "Dear [Client Name]" for formal relationships',
            'Use "Hi [First Name]" for casual relationships',
            'Avoid generic "To whom it may concern"',
            'Double-check spelling of client\'s name'
          ]
        },
        {
          heading: '3. Thank You Statement',
          content: 'Express gratitude for their business. A simple thank you goes a long way in building positive relationships.',
          tips: [
            'Thank them for their business or partnership',
            'Reference the specific project or service',
            'Keep it genuine and concise',
            'Example: "Thank you for choosing our services"'
          ]
        },
        {
          heading: '4. Clear Payment Instructions',
          content: 'Make it crystal clear how, when, and where to pay. Remove all friction from the payment process.',
          tips: [
            'State the total amount due clearly',
            'Specify the due date prominently',
            'List all accepted payment methods',
            'Include payment links or instructions',
            'Mention any early payment discounts'
          ]
        },
        {
          heading: '5. Invoice Summary',
          content: 'Provide a brief summary of what the invoice covers. This helps clients match the invoice to their records.',
          tips: [
            'List main services or products delivered',
            'Reference project name or purchase order number',
            'Include date range for services',
            'Keep summary to 2-3 sentences'
          ]
        },
        {
          heading: '6. Attached Invoice Document',
          content: 'Clearly mention that the invoice is attached. Use a professional PDF format with your branding.',
          tips: [
            'State "Please find the invoice attached"',
            'Use PDF format (not Word or Excel)',
            'Name file clearly: "Invoice_12345_ClientName.pdf"',
            'Ensure file size is under 2MB'
          ]
        },
        {
          heading: '7. Contact Information',
          content: 'Make it easy for clients to reach you if they have questions or need clarification.',
          tips: [
            'Include your direct email and phone number',
            'Add business hours if applicable',
            'Mention response time expectations',
            'Include link to support portal if available'
          ]
        },
        {
          heading: '8. Professional Sign-Off',
          content: 'End with a professional closing that reinforces your business relationship.',
          tips: [
            'Use "Best regards" or "Kind regards"',
            'Include full name and title',
            'Add company name',
            'Include email signature with logo'
          ]
        }
      ],
      conclusion: 'A well-crafted invoice email combines professionalism with clarity. It should make payment as easy as possible while maintaining a positive tone. Remember, your invoice email is part of your brand experience. Use templates to ensure consistency, but personalize when possible. Tools like InvoiceMeNow.com can help you create professional invoices with built-in email templates that follow these best practices.'
    }
  },
  'handle-past-due-invoice': {
    slug: 'handle-past-due-invoice',
    title: 'How to Handle a Past Due Invoice',
    category: 'Payment Collection',
    readTime: '7 min read',
    publishDate: '2024-02-05',
    image: 'https://images.pexels.com/photos/6694543/pexels-photo-6694543.jpeg?auto=compress&cs=tinysrgb&w=1200',
    content: {
      introduction: 'Past due invoices are a common challenge for businesses of all sizes. The key is to address them promptly and professionally while preserving your business relationships. This guide provides a step-by-step approach to handling overdue payments effectively.',
      sections: [
        {
          heading: '1. Understand the Timeline',
          content: 'Different stages of lateness require different approaches. Establish a clear timeline for follow-up actions.',
          tips: [
            '1-7 days overdue: Friendly reminder',
            '8-14 days overdue: Follow-up email',
            '15-30 days overdue: Phone call',
            '30+ days overdue: Formal notice or escalation'
          ]
        },
        {
          heading: '2. Check Your Records First',
          content: 'Before reaching out, verify all details. Ensure the invoice was sent correctly and that payment hasn\'t been received but not recorded.',
          tips: [
            'Confirm invoice was sent to correct email',
            'Check if payment was already received',
            'Verify invoice details are accurate',
            'Review original payment terms'
          ]
        },
        {
          heading: '3. Send a Polite Reminder (Days 1-7)',
          content: 'Start with the assumption that the late payment is an oversight. Keep your first reminder friendly and non-confrontational.',
          tips: [
            'Subject: "Friendly Reminder: Invoice #12345"',
            'Assume it was an oversight',
            'Reattach the original invoice',
            'Provide easy payment options'
          ]
        },
        {
          heading: '4. Second Follow-Up (Days 8-14)',
          content: 'If the first reminder goes unanswered, send a more direct follow-up. Be professional but firmer in tone.',
          tips: [
            'Reference your previous email',
            'State the exact overdue amount',
            'Ask if there are any issues',
            'Provide specific payment deadline'
          ]
        },
        {
          heading: '5. Make a Phone Call (Days 15-30)',
          content: 'A phone call often gets results when emails don\'t. It\'s personal, immediate, and harder to ignore.',
          tips: [
            'Be professional and calm',
            'Ask open-ended questions',
            'Listen to their concerns',
            'Document the conversation',
            'Confirm next steps in writing'
          ]
        },
        {
          heading: '6. Send a Formal Demand Letter (30+ days)',
          content: 'If all friendly attempts fail, send a formal demand letter. This shows you\'re serious and willing to take further action.',
          tips: [
            'Use official letterhead',
            'State the overdue amount clearly',
            'Set a final deadline (10-15 days)',
            'Mention potential consequences',
            'Send via certified mail for legal record'
          ]
        },
        {
          heading: '7. Consider Late Payment Fees',
          content: 'If your terms allow, apply late payment fees. This incentivizes timely payment and compensates for your collection efforts.',
          tips: [
            'Ensure late fees are in your original terms',
            'Use reasonable rates (1-1.5% per month)',
            'Clearly communicate fee structure',
            'Be willing to waive for good clients'
          ]
        },
        {
          heading: '8. Know When to Escalate',
          content: 'Sometimes professional collection help is necessary. Know when to bring in outside assistance.',
          tips: [
            'Collection agency for persistent non-payment',
            'Small claims court for amounts under $10,000',
            'Attorney for large amounts or complex cases',
            'Credit reporting for business debts'
          ]
        },
        {
          heading: '9. Protect Future Transactions',
          content: 'Once resolved, take steps to prevent future issues with this client.',
          tips: [
            'Require deposits for future work',
            'Shorten payment terms to Net 15',
            'Use progress billing for large projects',
            'Consider prepayment for repeat offenders'
          ]
        }
      ],
      conclusion: 'Handling past due invoices requires a systematic approach that escalates appropriately. Start friendly, be persistent, and escalate professionally. Most importantly, learn from each situation to improve your billing processes. Implement clear payment terms, use automated reminders, and maintain detailed records. Tools like InvoiceMeNow.com can help you track invoice status, automate reminders, and manage your accounts receivable more effectively.'
    }
  }
}

const ArticlePage = () => {
  const { slug } = useParams()
  const article = slug ? articles[slug] : null

  if (!article) {
    return <Navigate to="/blog" replace />
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Image */}
      <div className="h-96 overflow-hidden relative">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
        {/* Article Header */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 mb-12">
          <div className="flex items-center gap-4 mb-6">
            <span className="px-4 py-2 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
              {article.category}
            </span>
            <span className="text-gray-500">{article.readTime}</span>
            <span className="text-gray-500">
              {new Date(article.publishDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {article.title}
          </h1>

          <p className="text-xl text-gray-700 leading-relaxed">
            {article.content.introduction}
          </p>
        </div>

        {/* Article Sections */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-12">
          {article.content.sections.map((section, index) => (
            <div key={index} className="mb-10 last:mb-0">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                {section.heading}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                {section.content}
              </p>
              {section.tips && section.tips.length > 0 && (
                <ul className="space-y-3 ml-6">
                  {section.tips.map((tip, tipIndex) => (
                    <li key={tipIndex} className="flex items-start">
                      <span className="text-green-600 font-bold mr-3">✓</span>
                      <span className="text-gray-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Conclusion */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Final Thoughts
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {article.content.conclusion}
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 md:p-12 text-center text-white mb-12">
          <h2 className="text-3xl font-bold mb-4">Start Creating Professional Invoices</h2>
          <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
            Apply what you've learned. Generate professional invoices with automatic reminders and payment tracking.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg"
          >
            Create Invoice Now
          </Link>
        </div>

        {/* Back to Blog */}
        <div className="text-center mb-12">
          <Link
            to="/blog"
            className="text-blue-600 hover:text-blue-800 font-semibold text-lg"
          >
            ← Back to All Articles
          </Link>
        </div>
      </article>
    </div>
  )
}

export default ArticlePage
